import {cloneDeep} from 'lodash';
import {IScheduleGroup} from './schedule-group.interface';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {BaseService, DataBaseServiceResponse} from '../../services/base-service/base.service';
import {environment} from '../../../environments/environment';
import {firstValueFrom} from 'rxjs';

export interface IRTimetable {
  scheduleGroups: IScheduleGroup[];
  scheduleGroupSelected: string;
}

export namespace ScheduleGroupActions {
  export class GetByClinicId {
    static readonly type: string = '[TIMETABLE] Get Schedule Groups By Clinic Action';
    constructor(public clinicId: string) { }
  }

  export class Create {
    static readonly type: string = '[TIMETABLE] Create Schedule Group Action';
    constructor(public scheduleGroup: IScheduleGroup) { }
  }

  export class Update {
    static readonly type: string = '[TIMETABLE] Update Schedule Group Action';
    constructor(public id: string, public scheduleGroup: IScheduleGroup) { }
  }

  export class Remove {
    static readonly type: string = '[TIMETABLE] Remove Schedule Group Action';
    constructor(public id: string) { }
  }

  export class SetToClinic {
    static readonly type: string = '[TIMETABLE] Set Schedule Group To Clinic Action';
    constructor(public scheduleGroupId: string, public clinicId: string) { }
  }
}

@State<IRTimetable>({
  name: 'timetableState',
  defaults: {
    scheduleGroups: [],
    scheduleGroupSelected: null
  },
})
@Injectable()
export class TimetableState {
  constructor(private baseService: BaseService<any>) { }

  url = environment.SERVER_URL;

  @Selector()
  public static getScheduleGroups(state: IRTimetable): IScheduleGroup[] {
    return state.scheduleGroups;
  }

  @Selector()
  public static getScheduleGroupSelected(state: IRTimetable): IScheduleGroup {
    return state.scheduleGroups.find((group: IScheduleGroup) => group._id === state.scheduleGroupSelected);
  }

  @Action(ScheduleGroupActions.GetByClinicId)
  async getScheduleGroupsByClinicAction(ctx: StateContext<IRTimetable>, action: ScheduleGroupActions.GetByClinicId): Promise<void> {
    const response: DataBaseServiceResponse<IScheduleGroup[]> = await firstValueFrom((this.baseService.get(`${this.url}/schedule-groups/clinics/${action.clinicId}`, void 0)));
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      scheduleGroups: response.entity
    });
  }

  @Action(ScheduleGroupActions.Create)
  async createScheduleGroupAction(ctx: StateContext<IRTimetable>, action: ScheduleGroupActions.Create): Promise<void> {
    const response: DataBaseServiceResponse<IScheduleGroup> = await firstValueFrom(
      (this.baseService.post(`${this.url}/schedule-groups`, action.scheduleGroup))
    );
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      scheduleGroups: [...ctx.getState().scheduleGroups, response.entity],
      scheduleGroupSelected: response.entity._id
    });
  }

  @Action(ScheduleGroupActions.Update)
  async updateScheduleGroupAction(ctx: StateContext<IRTimetable>, action: ScheduleGroupActions.Update): Promise<void> {
    const response = await firstValueFrom((this.baseService.patch(`${this.url}/schedule-groups/${action.id}`, action.scheduleGroup)));
    if (response.error) throw new Error(response.message);
    const groups = cloneDeep(ctx.getState().scheduleGroups);
    const index = groups.findIndex(item => item._id === action.id);
    if (index !== -1) {
      groups[index] = action.scheduleGroup;
    }
    ctx.patchState({
      scheduleGroups: groups,
      scheduleGroupSelected: action.id
    });
  }

  @Action(ScheduleGroupActions.Remove)
  async removeScheduleGroupAction(ctx: StateContext<IRTimetable>, action: ScheduleGroupActions.Remove): Promise<void> {
    const response = await firstValueFrom((this.baseService.delete(`${this.url}/schedule-groups/${action.id}`, void 0)));
    if (response.error) throw new Error(response.message);
    const groups: IScheduleGroup[] = cloneDeep(ctx.getState().scheduleGroups);
    const index = groups.findIndex(item => item._id === action.id);
    if (index !== -1) {
      groups.splice(index, 1);
    }
    ctx.patchState({
      scheduleGroups: groups
    });
  }

  @Action(ScheduleGroupActions.SetToClinic)
  async setScheduleGroupToClinicAction(ctx: StateContext<IRTimetable>, action: ScheduleGroupActions.SetToClinic): Promise<void> {
    const response = await firstValueFrom(this.baseService.
      patch(`${this.url}/schedule-groups/${action.scheduleGroupId}/clinics/${action.clinicId}/set-default`, void 0));
    if (response.error) throw new Error(response.message);
  }
}
